import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';

import { BetManager, BettorApi, constants, DateService, NotificationManager } from '@atlas/core';
import { BetOutcomeCard, Button, Icon, Input, Label, Loader } from '@atlas/ui';

import BetOutcomeLink from 'common/BetOutcomeLink';
import Odds from 'common/Odds';

const { CANCELLED, CASH_OUT, LOSE, UNSETTLED, VOID, WIN } = constants.BET_STATUS;

const betCheckerInitialState = {
  error: null,
  loading: false,
  result: null,
};

const betCheckerReducer = (state, action) => ({
  ...state,
  ...action,
});

const BetCheckerCard = ({ result }) => {
  const { betId, betOdds, betSize, betStatus, betType, createTime, outcomes } = result;
  const [isCopied, setIsCopied] = useState(false);

  const betStatusClassName = cx('sub-title-bold-text status-label ', {
    'color-green': betStatus === WIN || betStatus === CASH_OUT,
    'color-grey': betStatus === UNSETTLED,
    'color-red': betStatus === LOSE || betStatus === CANCELLED,
    'color-yellow': betStatus === VOID,
  });

  const betToBetslip = useCallback(() => {
    NotificationManager.notify({
      message: 'betchecker_toast_title',
      status: 'success',
      type: 'notification',
    });

    // Cleaning previous bets
    BetManager.clearBets();

    outcomes.map(outcome => {
      BetManager.selectBet({
        betGroups: outcome.outcomeInfo.betMarketGroups,
        eventId: Number(outcome.outcomeInfo.eventId),
        marketId: Number(outcome.outcomeInfo.marketId),
        marketTypeId: Number(outcome.outcomeInfo.marketTypeId),
        outcomeId: Number(outcome.outcomeId),
      });
    });

    setIsCopied(true);
  }, [outcomes]);

  const buttonLabel = useMemo(() => (isCopied ? 'copied' : 'copy_to_betslip'), [isCopied]);

  const renderedOdds = useMemo(() => {
    if (betType === constants.BET_TYPE.SYSTEM) {
      return null;
    }

    return (
      <div>
        <span className="sub-title-text">
          <Label message="Total odds" />:
          <Odds value={betOdds} />
        </span>
      </div>
    );
  }, [betType, betOdds]);

  return (
    <div className="bet-report bet-checker-report">
      <div className="bet-report-header">
        <div>
          <span className="bet-order-header__type">
            &#8470;
            {betId}
            &nbsp;|&nbsp;
            <Label message={betType} />
            {betType === constants.BET_TYPE.SYSTEM && betSize}:
          </span>
          <Label className={betStatusClassName} message={betStatus} />
        </div>
      </div>

      {outcomes.map(outcome => (
        <BetOutcomeCard
          BetOutcomeLink={BetOutcomeLink}
          betType={betType}
          isShowMore
          key={outcome.outcomeId}
          outcome={outcome}
          showSportService
        />
      ))}

      <div className="show-more-bet-info">
        {renderedOdds}
        <div>
          <span className="sub-title-text">
            <Label message="Bet time" />:
            <span>{DateService.getFormattedDateWithTime(createTime)}</span>
          </span>
        </div>
      </div>

      <div className="bet-checker__copy">
        <Button disabled={isCopied} label={buttonLabel} onClick={betToBetslip} type="primary" />
      </div>
    </div>
  );
};

const BetChecker = () => {
  const [active, setActive] = React.useState(false);
  const [state, dispatch] = React.useReducer(betCheckerReducer, betCheckerInitialState);

  const handleButtonClick = useCallback(() => {
    dispatch({
      error: null,
      loading: true,
      result: null,
    });

    BettorApi.getPublicBetById(state.betId)
      .then(result => {
        dispatch({
          loading: false,
          result,
        });
      })
      .catch(error => {
        dispatch({
          error,
          loading: false,
        });
      });
  }, [state.betId]);

  const handleClear = () => {
    dispatch({ betId: '', result: null });
  };

  return (
    <>
      <div className="bet-checker-container">
        <div className="bet-checker__wrapper">
          <div className="bet-checker-input">
            <Input
              active={active}
              error={state.error}
              inputMode="numeric"
              label="betId"
              onBlur={() => setActive(false)}
              onChange={event => dispatch({ betId: event.target.value })}
              onFocus={() => setActive(true)}
              type="number"
              value={state.betId}
            />
            {state.betId && (
              <div className="icon-container" onClick={handleClear}>
                <Icon className="icon-close" name="close" />
              </div>
            )}
          </div>

          <Button
            block
            disabled={state.loading || !state.betId}
            onClick={handleButtonClick}
            size="big"
            type="primary"
          >
            {state.loading ? <Loader /> : <Label message="bet_check" />}
          </Button>
        </div>
        <div className="bet-checker__error">{state.error?.error}</div>
      </div>

      {state.result && <BetCheckerCard result={state.result} />}
    </>
  );
};

export default BetChecker;
